import { useState } from "react";

interface SummaryCard {
  label: string;
  value: string;
  icon: React.ReactNode;
  accentClass: string;
  tooltip?: string;
}

interface SummaryCardsProps {
  totalProperties: number;
  totalMonthlyRent: number;
  pendingPayments: number;
  overduePayments: number;
  collectionRate?: number;
}

export function SummaryCards({
  totalProperties,
  totalMonthlyRent,
  pendingPayments,
  overduePayments,
  collectionRate = 0,
}: SummaryCardsProps) {
  const [openTooltipKey, setOpenTooltipKey] = useState<string | null>(null);

  const cards: SummaryCard[] = [
    {
      label: "Total Properties",
      value: String(totalProperties),
      accentClass: "bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400",
      icon: (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 21v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21m0 0h4.5V3.545M12.75 21h7.5V10.75M2.25 21h1.5m18 0h-18M2.25 9l4.5-1.636M18.75 3l-1.5.545m0 6.205 3 1m1.5.5-1.5-.5M6.75 7.364V3h-3v18m3-13.636 10.5-3.819" />
        </svg>
      ),
    },
    {
      label: "Monthly Rent",
      value: `₹${totalMonthlyRent.toLocaleString("en-IN")}`,
      accentClass: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400",
      icon: (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z" />
        </svg>
      ),
    },
    {
      label: "Pending",
      value: String(pendingPayments),
      accentClass: "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
      tooltip: "Number of units with rent not yet paid for the current month. Rent is due by each unit’s due date (e.g. 5th of the month).",
      icon: (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
      ),
    },
    {
      label: "Overdue",
      value: String(overduePayments),
      accentClass: "bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400",
      tooltip: "Number of units whose rent due date for the current month has already passed and is still unpaid.",
      icon: (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
        </svg>
      ),
    },
  ];

  return (
    <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
      {cards.map((card) => (
        <div
          key={card.label}
          className="relative rounded-2xl border border-slate-200/80 dark:border-slate-700/80 bg-white dark:bg-slate-800/95 p-4 sm:p-5 shadow-sm dark:shadow-none hover:shadow-md dark:hover:shadow-none transition-shadow overflow-hidden"
        >
          <div className={`absolute right-3 bottom-2 opacity-[0.07] dark:opacity-[0.08] [&>svg]:h-16 [&>svg]:w-16 sm:[&>svg]:h-20 sm:[&>svg]:w-20 ${card.accentClass}`}>
            {card.icon}
          </div>
          <p className="relative text-xs font-medium text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-1">
            {card.label}
            {card.tooltip && (
              <button
                type="button"
                onClick={() => setOpenTooltipKey((k) => (k === card.label ? null : card.label))}
                className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-slate-200/80 dark:bg-slate-600/80 text-slate-500 dark:text-slate-400 hover:bg-slate-300 dark:hover:bg-slate-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 touch-manipulation"
                title={card.tooltip}
                aria-expanded={openTooltipKey === card.label}
                aria-label="Show explanation"
              >
                <span className="text-[10px] font-bold leading-none">?</span>
              </button>
            )}
          </p>
          {card.tooltip && openTooltipKey === card.label && (
            <p
              className="relative text-xs text-slate-600 dark:text-slate-300 mb-2 p-2 rounded-lg bg-slate-100 dark:bg-slate-700/80 border border-slate-200 dark:border-slate-600"
              role="tooltip"
            >
              {card.tooltip}
            </p>
          )}
          <p className="relative text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100">
            {card.value}
          </p>
        </div>
      ))}
      {collectionRate > 0 && (
        <div className="col-span-2 lg:col-span-4 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <span>Collection rate this year:</span>
          <span className="font-semibold text-slate-700 dark:text-slate-300">{collectionRate}%</span>
        </div>
      )}
    </div>
  );
}
